import React, { useMemo, useState, useEffect } from 'react';
import {
    FlexDiv,
    Heading4,
    TextInput,
    ThemedButton,
    palette,
    TextLead,
    TextTiny,
    Block,
} from '@awarego/awarego-components';
import styled, { css } from 'styled-components';
import { useStores } from '../../hooks/use-stores';
import { useTranslation, Trans } from 'react-i18next';
import Lottie from 'react-lottie';
import animationData from './anim.json';
import { observer } from 'mobx-react';
import { Language as LanguageIcon } from '@material-ui/icons';
import { IconButton, Menu, MenuItem } from '@material-ui/core';

const Logo = styled.div`
    img {
        max-height: 50px;
        max-width: 130px;
    }
    padding-bottom: 24px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: ${(props) => {
        if (props.position === 'left') return 'flex-start';
        if (props.position === 'right') return 'flex-end';
        return 'center';
    }};
    ${(props) =>
        props.noMargin &&
        css`
            margin: 0;
        `}
`;

function LandingPageShort({
    onSubmit,
    onEmailChange,
    email,
    error,
    joining,
    variant,
    emailOption,
    language,
    availableLanguages,
}) {
    const { brandingStore, store, commonStore } = useStores();
    const { logo, account_name, brandingLoaded, privacyUrl } = brandingStore;
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState(null);
    const [languages, setLanguages] = useState([]);

    useEffect(() => {
        if (availableLanguages && availableLanguages.length > 0) {
            const readableLanguages = availableLanguages.map((x) => ({
                id: typeof x === 'string' ? x : x.code,
                name: commonStore.languagesIndex[
                    typeof x === 'string' ? x : x.code
                ]
                    ? commonStore.languagesIndex[
                          typeof x === 'string' ? x : x.code
                      ].english_name
                    : typeof x === 'string'
                    ? x
                    : x.code,
            }));
            setLanguages(readableLanguages);
        }
    }, [availableLanguages, commonStore.languagesIndex]);

    const handleLanguageClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLanguageClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageSelect = async (languageCode) => {
        await store.setLanguage(languageCode, true);
        handleLanguageClose();
    };

    const animationDefaultOptions = useMemo(
        () => ({
            loop: false,
            autoplay: true,
            animationData: animationData,
            rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice',
            },
            speed: 1,
        }),
        []
    );

    const logoContainer = useMemo(
        () => <img src={logo} alt={t('logoAlt', account_name)} />,
        [logo, account_name, t]
    );

    return (
        <FlexDiv
            justifyCenter
            alignCenter
            style={{
                position: 'relative',
                width: '100%',
                height: '100vh',
            }}
        >
            <Lottie
                style={{ cursor: 'default' }}
                options={animationDefaultOptions}
                height={700}
                width={700}
            />

            <Block
                resetMargin
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    padding: '32px 24px 32px 24px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    background: '#fff',
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                    textAlign: 'start',
                }}
            >
                {availableLanguages &&
                    availableLanguages.length > 1 &&
                    !brandingStore.hideLanguageList && (
                        <>
                            <IconButton
                                onClick={handleLanguageClick}
                                style={{
                                    position: 'absolute',
                                    top: '6px',
                                    right: '6px',
                                    padding: '8px',
                                }}
                                size="small"
                            >
                                <LanguageIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleLanguageClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                {languages.map((lang) => (
                                    <MenuItem
                                        key={lang.id}
                                        onClick={() =>
                                            handleLanguageSelect(lang.id)
                                        }
                                        selected={lang.id === store.language}
                                    >
                                        {lang.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                <Logo
                    position={
                        brandingStore.branding &&
                        brandingStore.branding[
                            'ui.minihra_landing_logo_position'
                        ]
                    }
                >
                    {logoContainer}
                </Logo>
                <FlexDiv>
                    <TextLead color={palette.graphite.charcoal} mb={8}>
                        {t('Test your cybersecurity knowledge and behavior')}{' '}
                    </TextLead>
                </FlexDiv>

                <form onSubmit={onSubmit}>
                    {emailOption !== 'no-email' && (
                        <>
                            <Heading4
                                fontSizeRem={1.5}
                                color={palette.graphite.heavyMetal}
                                style={{
                                    fontFamily:
                                        'EuclidCircularA-Regular, sans-serif',
                                    fontWeight: '500',
                                }}
                            >
                                {' '}
                                {t('Enter your email')}{' '}
                                {emailOption === 'required-email'
                                    ? ''
                                    : `(${t('optional')})`}
                            </Heading4>
                            <FlexDiv mt={6} mb={6}>
                                <TextInput
                                    darkerInputBorder
                                    placeholder={t('Email')}
                                    stretched
                                    value={email}
                                    onChange={onEmailChange}
                                    name="email"
                                    error={error}
                                    type="email"
                                    helperText={error}
                                    overwriteGlobalStyles
                                />
                            </FlexDiv>
                        </>
                    )}
                    <FlexDiv mb={12}>
                        <ThemedButton
                            style={{
                                marginBottom: '8px',
                                fontFamily:
                                    'EuclidCircularA-Regular, sans-serif',
                                fontWeight: '500',
                            }}
                            disabled={joining}
                            mode="primary"
                            size="fullWidth"
                            type="submit"
                            text={t("Let's go!")}
                        />
                    </FlexDiv>
                </form>
                <TextTiny
                    color={palette.graphite.ash}
                    style={{ maxWidth: '330px' }}
                >
                    <Trans
                        components={{
                            Link: (
                                <a
                                    href={
                                        (brandingLoaded && privacyUrl) ||
                                        'https://awarego.com/privacy/'
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ textDecoration: 'underline' }}
                                />
                            ),
                        }}
                    >
                        {t(
                            'For further details see our <Link>Privacy Policy</Link>.'
                        )}
                    </Trans>
                </TextTiny>
            </Block>
        </FlexDiv>
    );
}

export default observer(LandingPageShort);
